import { useEffect, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import type { BeneficiarioRelacion, TipoDocumento } from '@/lib/typesNeoCore'

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  /** Si el asociado tiene FEIC, el form pide datos extra (DPI, fecha nac, dirección). */
  requiereFeic: boolean
  /** Suma de % vigente actual — para validar no pasarse de 100. */
  porcentajeAsignado: number
  onSuccess: () => void
}

const RELACIONES: Array<{ value: BeneficiarioRelacion; label: string }> = [
  { value: 'CONYUGE',    label: 'Cónyuge' },
  { value: 'PAREJA',     label: 'Pareja' },
  { value: 'HIJO',       label: 'Hijo' },
  { value: 'HIJA',       label: 'Hija' },
  { value: 'PADRE',      label: 'Padre' },
  { value: 'MADRE',      label: 'Madre' },
  { value: 'HERMANO',    label: 'Hermano' },
  { value: 'HERMANA',    label: 'Hermana' },
  { value: 'ABUELO',     label: 'Abuelo' },
  { value: 'ABUELA',     label: 'Abuela' },
  { value: 'NIETO',      label: 'Nieto' },
  { value: 'NIETA',      label: 'Nieta' },
  { value: 'TIO',        label: 'Tío' },
  { value: 'TIA',        label: 'Tía' },
  { value: 'SOBRINO',    label: 'Sobrino' },
  { value: 'SOBRINA',    label: 'Sobrina' },
  { value: 'PRIMO',      label: 'Primo' },
  { value: 'PRIMA',      label: 'Prima' },
  { value: 'PADRASTRO',  label: 'Padrastro' },
  { value: 'MADRASTRA',  label: 'Madrastra' },
  { value: 'HIJASTRO',   label: 'Hijastro' },
  { value: 'HIJASTRA',   label: 'Hijastra' },
  { value: 'AMIGO',      label: 'Amigo / Amiga' },
  { value: 'OTRO',       label: 'Otro' },
]

type Form = {
  nombre_completo: string
  relacion: BeneficiarioRelacion | ''
  porcentaje: string
  telefono: string
  // Solo FEIC:
  tipo_documento: TipoDocumento | ''
  numero_documento: string
  fecha_nacimiento: string
  email: string
  direccion: string
}

const INIT: Form = {
  nombre_completo: '',
  relacion: '',
  porcentaje: '',
  telefono: '',
  tipo_documento: 'DPI',
  numero_documento: '',
  fecha_nacimiento: '',
  email: '',
  direccion: '',
}

export function BeneficiarioModal({
  open, onClose, asociadoId, requiereFeic, porcentajeAsignado, onSuccess,
}: Props) {
  const [form, setForm] = useState<Form>(INIT)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setForm(INIT)
    setError(null)
  }, [open])

  const update = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const porcentajeNum = parseFloat(form.porcentaje) || 0
  const disponibleRestante = Math.max(0, 100 - porcentajeAsignado)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!form.nombre_completo.trim()) {
      setError('Indicá el nombre completo del beneficiario.')
      return
    }
    if (!form.relacion) {
      setError('Seleccioná la relación con el asociado.')
      return
    }
    if (!porcentajeNum || porcentajeNum <= 0 || porcentajeNum > 100) {
      setError('El porcentaje debe estar entre 1 y 100.')
      return
    }
    if (porcentajeAsignado + porcentajeNum > 100) {
      setError(
        `Solo queda ${disponibleRestante.toFixed(0)}% disponible. ` +
        `Si querés asignar más, ajustá los beneficiarios existentes primero.`
      )
      return
    }
    if (requiereFeic) {
      if (!form.numero_documento.trim()) {
        setError('Para FEIC: indicá el documento del beneficiario.')
        return
      }
      if (form.tipo_documento === 'DPI' && !/^\d{13}$/.test(form.numero_documento)) {
        setError('El DPI debe tener 13 dígitos.')
        return
      }
    }

    setSubmitting(true)
    const payload: Record<string, unknown> = {
      nombre_completo: form.nombre_completo.toUpperCase(),
      relacion: form.relacion,
      porcentaje: form.porcentaje,
      telefono: form.telefono || null,
    }
    if (requiereFeic) {
      payload.tipo_documento = form.tipo_documento || null
      payload.numero_documento = form.numero_documento || null
      payload.fecha_nacimiento = form.fecha_nacimiento || null
      payload.email = form.email || null
      payload.direccion = form.direccion || null
    }

    const { error: rpcErr } = await supabase.rpc('crear_beneficiario', {
      p_asociado_id: asociadoId,
      p_input: payload,
    })
    setSubmitting(false)
    if (rpcErr) {
      setError(`No se pudo guardar: ${rpcErr.message}`)
      return
    }
    onSuccess()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Agregar beneficiario"
      subtitle={`${disponibleRestante.toFixed(0)}% disponible para asignar`}
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="beneficiario-form"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Guardando…' : 'Agregar beneficiario'}
          </button>
        </>
      }
    >
      <form id="beneficiario-form" onSubmit={onSubmit} className="form-grid">
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        <div className="form-section">
          <div className="form-section-grid">
            <label className="form-field" style={{ gridColumn: 'span 2' }}>
              <span className="form-label">Nombre completo</span>
              <input
                value={form.nombre_completo}
                onChange={(e) => update('nombre_completo', e.target.value)}
                placeholder="María Ramírez de Pérez"
                autoFocus
              />
            </label>

            <label className="form-field">
              <span className="form-label">Relación</span>
              <select value={form.relacion} onChange={(e) => update('relacion', e.target.value as BeneficiarioRelacion)}>
                <option value="">—</option>
                {RELACIONES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>

            <label className="form-field">
              <span className="form-label">Porcentaje (%)</span>
              <input
                value={form.porcentaje}
                onChange={(e) => update('porcentaje', e.target.value.replace(/[^\d.]/g, ''))}
                placeholder={disponibleRestante > 0 ? String(disponibleRestante.toFixed(0)) : '50'}
                inputMode="decimal"
              />
            </label>

            <label className="form-field" style={{ gridColumn: 'span 2' }}>
              <span className="form-label">Teléfono</span>
              <input
                value={form.telefono}
                onChange={(e) => update('telefono', e.target.value)}
                placeholder="+50250000000"
              />
            </label>
          </div>
        </div>

        {requiereFeic && (
          <div className="form-section">
            <div className="form-section-header">
              <h3 className="form-section-title">Datos adicionales (FEIC)</h3>
              <p className="form-section-hint">
                Como este asociado tiene expediente FEIC, el regulador pide identificar
                a sus beneficiarios con detalle.
              </p>
            </div>
            <div className="form-section-grid">
              <label className="form-field">
                <span className="form-label">Tipo de documento</span>
                <select value={form.tipo_documento} onChange={(e) => update('tipo_documento', e.target.value as TipoDocumento)}>
                  <option value="DPI">DPI</option>
                  <option value="PASAPORTE">Pasaporte</option>
                  <option value="CARNET_RESIDENTE">Carnet residente</option>
                </select>
              </label>
              <label className="form-field">
                <span className="form-label">Número de documento</span>
                <input
                  value={form.numero_documento}
                  onChange={(e) => update('numero_documento', e.target.value.replace(/\D/g, '').slice(0, 13))}
                  placeholder="1234567890101"
                  inputMode="numeric"
                />
              </label>
              <label className="form-field">
                <span className="form-label">Fecha de nacimiento</span>
                <input type="date" value={form.fecha_nacimiento} onChange={(e) => update('fecha_nacimiento', e.target.value)} />
              </label>
              <label className="form-field">
                <span className="form-label">Email</span>
                <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} />
              </label>
              <label className="form-field" style={{ gridColumn: 'span 2' }}>
                <span className="form-label">Dirección</span>
                <input value={form.direccion} onChange={(e) => update('direccion', e.target.value)} />
              </label>
            </div>
          </div>
        )}
      </form>
    </Modal>
  )
}
